/**
 * Componente Modal reutilizable
 * @param {boolean} isOpen - Controla visibilidad del modal
 * @param {Function} onClose - Callback al cerrar
 * @param {string} title - Título del modal
 * @param {React.ReactNode} children - Contenido del modal
 * @param {string} size - 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} variant - 'default' | 'dark' (gris semi-transparente)
 * @param {boolean} showCloseButton - Mostrar botón de cerrar
 * @param {Array} footerButtons - Array de { label, onClick, variant }
 * @param {string} className - Clases adicionales para el contenido
 */
function Modal({
  isOpen = false,
  onClose,
  title,
  children,
  size = 'md',
  variant = 'default',
  showCloseButton = true,
  footerButtons = [],
  className = '',
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  const isDark = variant === 'dark';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`${variant === 'default' ? 'bg-white' : ''} rounded-lg shadow-xl w-full ${sizeClasses[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
          <h2 id="modal-title" className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {title}
          </h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className={`p-1 rounded-md transition-colors ${isDark ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              aria-label="Cerrar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {/* Body */}
        <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>
        {/* Footer */}
        {(footerButtons.length > 0 || showCloseButton) && (
          <div className={`flex justify-end gap-2 p-4 border-t ${isDark ? 'border-white/20' : 'border-gray-200'}`}>
            {footerButtons.map((btn, idx) => (
              <button
                key={idx}
                onClick={btn.onClick}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  btn.variant === 'primary'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : btn.variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : isDark
                    ? 'bg-white/20 hover:bg-white/30 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                {btn.label}
              </button>
            ))}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${isDark ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
              >
                Cerrar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
